import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import DeleteModal from '@components/DeleteModal';
import LoaderComponent from '@components/LoaderComponent';
import NitexImageCropper from '@components/NitexImageCropper';
import { toastError, toastInfo, toastSuccess, toastWarning } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import UpdateFittingModal from '@features/designTool/components/UpdateFittingModal';
import BuyerAndNitexMeasurementTable from '@features/measurement/components/BuyerAndNitexMeasurementTable';
import ConfirmationModal from '@features/measurement/components/ConfirmationModal';
import CostingAndPPSampleTable from '@features/measurement/components/CostingAndPPSampleTable';
import ViewHistory from '@features/measurement/components/ViewHistory';
import AddPomFromRightPnael from '@features/pom/components/AddPomFromRightPnael';
import BlockSection from '@features/design/components/BlockSection';

import { setCurrentBlockAction } from '@store/actions/BlockAction';
import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	addMeasurementGroupGrading,
	changeCostingPpSampleBaseSize,
	clearMeasurementChangeLog,
	clearMeasurementTable,
	convertMeasurementSizeType,
	convertMeasurementSizeTypeChangeLog,
	fetchAllSizeCategories,
	fetchPomAndSize,
	fetchProductWiseMeasurementSizeInfo,
	hideMeasurementTableColumn,
	onAddChangeLogOnDuplicate,
	onUpdateMeasurementTable,
	removeLibraryPointInMeasurementTable,
	replaceProductMeasurementChangeLog,
	saveNewPointInMeasurementTable,
	saveProductMeasurementChangeLog,
	setInitMeasurementTableId,
	setMeasurementHistoryInfo,
	showAllMeasurementTableColumn,
	toggleBlockUsedFromInfo,
	toggleMeasurementActualAndDiffColumn,
	updateSizeWiseValue
} from '@store/actions/MeasurementAction';
import {
	fetchAllTemplate,
	fetchProductMeasurement,
	fetchProductSteps,
	upDateDesignDetails
} from '@store/design/action';
import * as actionTypes from '@store/design/action-types';
import { getPomAndSize, useMeasurement } from '@store/reducers';
import { Types } from '@store/types';

import { fetchEnumValues } from '@services/commonService';
import {
	ApproveOrRejectMeasurementTable,
	copyMeasurementType,
	downloadMeasurementInExcel,
	downloadMeasurementInPDF,
	duplicateMeasurement,
	fetchMeasurementHistoryList,
	getFittingResponse,
	increaseSizeCategorySequence,
	initMeasurementTable,
	loadPomTemplate,
	saveMeasurementTable,
	setCurrentMeasurementType,
	setMeasurementShowColumn,
	showAllHiddenSizeColumn
} from '@services/measurementService';

import ApiClient from '@lib/apiClient';

import { getPrimaryUserType, onGetLocalFromUtc, ORGANIZTION_INFO, printApiError } from '@utils/Util';

const Measurement = ({ productId, data, reference, isLoading, setIsLoading, designToolStoreData }) => {
	const [selectedDesignData, setSelectedDesignData] = useState(designToolStoreData?.blockDetailsResponse);
	const initialMeasurementTableData = useMeasurement()?.initialMeasurementTableData || [];
	const [tmpSelectedMeasurementType, setTmpSelectedMeasurementType] = useState({});
	const [selectedMeasurementType, setSelectedMeasurementType] = useState({});
	const [initialMeasurementUnit, setInitialMeasurementUnit] = useState(null);
	const [isMeasurementEditable, setIsMeasurementEditable] = useState(false);
	const [openConfirmationModal, setConfirmationModal] = useState(false);
	const [isShowConfirmation, setIsShowConfirmation] = useState(false);
	const hasShowColumn = useMeasurement()?.hasShowActualAndDiffColumn;
	const isShowBlockUsed = useMeasurement()?.isShowBlockUsedFrom || false;
	const [templateNamePopUp, setTemplateNamePopUp] = useState(false);
	const [isEditFittingInfo, setIsEditFittingInfo] = useState(false);
	const sizeCategorySequence = useMeasurement()?.sizeCategorySequence || {};
	const pomAndSizeList = useSelector(getPomAndSize)?.measurementInfo || {};
	const hiddenSizeIds = useMeasurement()?.hiddenColumnIds || [];
	const [measurementImage, setMeasurementImage] = useState(null);
	const [confirmationTitle, setConfirmationTitle] = useState('');
	const [measurementTypes, setMeasurementTypes] = useState([]);
	const [fittingResponse, setFittingResponse] = useState(null);
	const [confirmationType, setConfirmationType] = useState('');
	const [designIdToView, setDesignIdToView] = useState(null);
	const measurementTable = useMeasurement()?.measurementTable || [];
	const fitWiseSizeList = useMeasurement()?.fitWiseSizeList || {};
	const [openHistory, setOpenHistory] = useState(false);
	const [tmpUnitType, setTmpUnitType] = useState('');
	const [openPanel, setOpenPanel] = useState(false);
	const [isDelete, setIsDelete] = useState(false);
	const historyInfo = useMeasurement()?.historyInfo || {};
	const [unitType, setUnitType] = useState('');
	const [isEdit, setIsEdit] = useState(false);
	const [isOpenSizeConvertDropdwon, setOpenSizeConvertDropdown] = useState(false);
	const changeLog = useMeasurement()?.changeLog || [];
	const [pomId, setPomId] = useState(null);
	const pomAndSizeRef = useRef(null);
	const changeLogRef = useRef([]);
	const saveRef = useRef(null);
	const dispatch = useDispatch();
	const { id } = useParams();
	const initMeasurementId = useMeasurement()?.initId;
	const [selectedPomResponseIds, setSelectedPomResponseIds] = useState([]);
	const [isBottomCategory] = useState(selectedDesignData?.parentCategory === 'BOTTOM' ? true : false);
	const [showReplaceDropdownId, setShowReplaceDropdownId] = useState(null);
	const [historyList, setHistoryList] = useState([]);
	const designToolStore = useSelector((store) => store.designToolStore);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		let pomIds = measurementTable?.map((item) => item.pomResponse.libraryPOMId || item.pomResponse.id);
		setSelectedPomResponseIds(pomIds);
	}, [measurementTable]);

	useEffect(() => {
		return () => {
			dispatch({
				type: actionTypes.CLEAR_POM_AND_SIZE,
				payload: {}
			});
			dispatch(clearMeasurementTable());
		};
	}, []);

	const getMeasurementHistoryList = async () => {
		try {
			dispatch(openLoader());
			const data = await fetchMeasurementHistoryList(id, selectedMeasurementType?.constant);
			setHistoryList(data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (openHistory) {
			getMeasurementHistoryList();
		}
	}, [selectedMeasurementType?.constant, openHistory]);

	useEffect(() => {
		changeLogRef.current = changeLog;
	}, [changeLog]);

	useEffect(() => {
		pomAndSizeRef.current = pomAndSizeList;
	}, [pomAndSizeList]);

	useEffect(() => {
		return () => {
			if (changeLogRef?.current?.length > 0) {
				try {
					dispatch(openLoader());
					saveChangeLogs().then(() => {
						dispatch(closeLoader());
					});
				} catch {
					dispatch(closeLoader());
				}
			}
		};
	}, [id]);

	useEffect(() => {
		setSelectedDesignData(designToolStoreData);
		return () => {
			dispatch(setCurrentBlockAction(null));
		};
	}, [designToolStoreData]);

	useEffect(() => {
		setSelectedMeasurementType(pomAndSizeList.measurementType);
	}, [pomAndSizeList.measurementType]);

	useEffect(() => {
		setUnitType(pomAndSizeList.unit);
		setInitialMeasurementUnit(pomAndSizeList.unit);
	}, [pomAndSizeList.unit]);

	useClickOutside(saveRef, () => {
		setTemplateNamePopUp(!templateNamePopUp);
	});

	const onRestoreHistory = async (id) => {
		dispatch(openLoader());
		try {
			await ApiClient.POST('measurementRestoreHistory', {}, `${id}/restore`);
			getMeasurementHistoryList();
			setHistoryList(data);
			await fetchMeasurementInfo();
			await fetchFittingResponse();
			toastSuccess('Restore completed successfully!');
		} catch (e) {
			printApiError(e);
		} finally {
			dispatch(closeLoader());
		}
	};

	const fetchFittingResponse = async () => {
		try {
			dispatch(openLoader());
			const data = await getFittingResponse(id);
			setFittingResponse(data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		(async () => {
			try {
				dispatch(openLoader());
				const data = await fetchEnumValues('info.nitex.app.measurement.MeasurementType?sorted=true');
				setMeasurementTypes(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		})();
	}, [id]);

	const fetchMeasurementInfo = async () => {
		try {
			await dispatch(
				fetchProductWiseMeasurementSizeInfo(id, selectedMeasurementType?.constant, unitType)
			);
		} catch (e) {
			printApiError(e);
		}
	};

	useEffect(() => {
		(async () => {
			try {
				dispatch(openLoader());
				await fetchMeasurementInfo();
				await fetchFittingResponse();
			} catch {
				dispatch(closeLoader());
			} finally {
				dispatch(closeLoader());
			}
		})();
	}, [selectedMeasurementType?.constant, unitType]);

	useEffect(() => {
		if (id) {
			dispatch(fetchPomAndSize(id)).finally(() => {
				setIsLoading(false);
			});
			dispatch(fetchAllTemplate()).finally(() => {
				setIsLoading(false);
			});
		}
	}, [id]);

	useEffect(() => {
		setMeasurementImage(data);
	}, [data]);

	const onRemovePom = async (productId, pomId) => {
		setIsLoading(true);
		setConfirmationModal(false);
		await ApiClient.POST('removePomFromProduct', {
			measurementUnit: unitType,
			pointOfMeasurementId: pomId,
			productId: productId
		})
			.then(({ data }) => {
				setPomId(null);
				setIsLoading(false);
				if (data) {
					dispatch(fetchProductSteps(productId));
					dispatch(fetchProductMeasurement(productId, unitType));
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onCroppedDataSave = (base64str) => {
		if (base64str) {
			let croppedBodyData = {
				id: measurementImage.id,
				base64Str: '',
				docMimeType: 'image/jpeg',
				extension: 'jpeg'
			};
			setIsLoading(true);
			dispatch(upDateDesignDetails({ ...croppedBodyData, base64Str: base64str }, productId)).finally(
				() => {
					setIsLoading(false);
					setIsEdit(false);
				}
			);
		} else {
			toastError('Crop was not successful');
			setIsEdit(false);
		}
	};

	const handleDelete = async () => {
		setIsLoading(true);
		await ApiClient.DELETE('updateProductMeasurement', {}, `${id}`)
			.then(({ data }) => {
				dispatch(fetchProductMeasurement(productId, unitType)).finally(() => {
					setIsLoading(false);
					setIsDelete(false);
				});
				toastSuccess(data.message);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const updateFitInfoItem = (item, data) => {
		const updatedItem = JSON.parse(JSON.stringify(item));
		if (updatedItem.fitInfo && updatedItem.fitInfo.FIRST_FIT) {
			updatedItem.fitInfo.FIRST_FIT.ordering += 1;
			updatedItem.fitInfo.FIRST_FIT.pomResponseId = data.id;
		}
		return updatedItem;
	};

	// const updateFitInfoItemIndex = (item, index) => {
	// 	const updatedItem = JSON.parse(JSON.stringify(item));
	// 	if (updatedItem.fitInfo && updatedItem.fitInfo.FIRST_FIT) {
	// 		updatedItem.fitInfo.FIRST_FIT.ordering = index + 1;
	// 	}
	// 	return updatedItem;
	// };

	const onAddChangeLog = (item, logType, fieldName, value, fitTypeId) => {
		let changeLogInfo = {
			changeLogType: logType,
			id: fitTypeId,
			dto: {
				pointOfMeasurementId: item.pointOfMeasurementId,
				[fieldName]: value || null
			}
		};
		return changeLogInfo;
	};

	const onAddSizeChangeLog = (item, size) => {
		return {
			changeLogType: 'MEASUREMENT_VALUE_UPDATE',
			id: item.fitTypeId,
			dto: {
				pointOfMeasurementId: item.pointOfMeasurementId,
				value: size?.value || null,
				sizeId: size?.id
			}
		};
	};

	const onGetGradingChangeLogs = (item) => {
		const gradingChangeLogs = [];
		const gradings = ['grading1', 'grading2', 'grading3'];
		const gradingNumbers = ['FIRST', 'SECOND', 'THIRD'];
		gradings.forEach((grading, index) => {
			if (item?.[grading]) {
				gradingChangeLogs.push({
					changeLogType: 'GRADING_ADD',
					id: item?.fitTypeId,
					dto: {
						pointOfMeasurementId: item.pointOfMeasurementId,
						value: item[grading] || null,
						gradingNumber: gradingNumbers[index],
						gradingType: item?.gradingType?.constant
					}
				});
			}
		});
		return gradingChangeLogs;
	};

	const onGetGradingActionChangeLogs = (item) => {
		let changeLogInfo = {
			changeLogType: 'GRADING_ACTION_CHANGE',
			id: item?.fitTypeId,
			dto: {
				pointOfMeasurementId: item?.pointOfMeasurementId,
				gradingType: item?.gradingType?.constant
			}
		};
		return changeLogInfo;
	};

	const onGetActualValueUpdate = (item) => {
		let changeLogInfo = {
			changeLogType: 'ACTUAL_VALUE_UPDATE',
			id: item?.fitTypeId,
			dto: {
				pointOfMeasurementId: item?.pointOfMeasurementId,
				value: item?.actualValue || null
			}
		};
		return changeLogInfo;
	};

	const onAddDuplicateChangLogs = async (item, newMeasurementTable) => {
		let duplicateChangeLog = {
			changeLogType: 'DUPLICATE_POINT',
			id: item?.fitTypeId,
			dto: {
				pointOfMeasurementId: item?.pointOfMeasurementId
			}
		};
		let addedChangeLogs = [...changeLog, duplicateChangeLog];

		let orderingChangeLog = {
			changeLogType: 'PRODUCT_MEASUREMENT_ORDERING_UPDATE',
			orderingRequests: newMeasurementTable?.map((item, index) => ({
				pointOfMeasurementId: item.pomResponse.id,
				sl: index
			}))
		};
		if (item?.tolerance) {
			addedChangeLogs.push(
				onAddChangeLog(item, 'TOLERANCE_UPDATE', 'value', item?.tolerance, item?.fitTypeId)
			);
		}
		if (item?.remarks) {
			addedChangeLogs.push(
				onAddChangeLog(item, 'POINT_REMARKS_UPDATE', 'remarks', item?.remarks, item?.fitTypeId)
			);
		}
		if (item?.sizeValueList?.length > 0) {
			item.sizeValueList.map((size) => {
				addedChangeLogs.push(onAddSizeChangeLog(item, size));
			});
		}

		let updatedChangeLogs = [
			...addedChangeLogs,

			...(selectedMeasurementType?.constant === 'PP_SAMPLE' ? onGetGradingChangeLogs(item) : []),
			...(selectedMeasurementType?.constant === 'PP_SAMPLE'
				? [onGetGradingActionChangeLogs(item)]
				: []),
			onGetActualValueUpdate(item),
			orderingChangeLog
		];
		await dispatch(onAddChangeLogOnDuplicate(updatedChangeLogs));
	};

	const onDuplicateMeasurement = async (item, index) => {
		try {
			const data = await duplicateMeasurement(item?.pomResponse?.id);
			if (data) {
				const newArray = [...measurementTable];
				newArray.splice(index + 1, 0, {
					...item,
					...updateFitInfoItem(item, data),
					pomResponse: data
				});
				onAddDuplicateChangLogs(
					{
						pointOfMeasurementId: data.id,
						...item.fitInfo.FIRST_FIT
					},
					newArray
				);
				dispatch(onUpdateMeasurementTable(newArray));
			}
		} catch (error) {
			toastError(error);
		}
	};

	const isEditable = () => {
		return isMeasurementEditable && !historyInfo?.id;
	};

	const formatChangeLog = (selectedSizeToConvert, fitId, oldSizeInfo) => {
		const convertedChangeLogInfo = {
			changeLogType: 'SIZE_REPLACE',
			id: fitId,
			dto: {
				sizeId: selectedSizeToConvert?.id,
				oldSizeId: oldSizeInfo?.id
			}
		};
		const addChangeLogInfo = {
			changeLogType: 'SIZE_ADD',
			id: fitId || null,
			dto: {
				sizeId: selectedSizeToConvert.id
			}
		};
		const addBaseSize = {
			changeLogType: 'BASE_SIZE_UPDATE',
			id: fitId || null,
			dto: {
				sizeId: fitWiseSizeList?.FIRST_FIT?.baseSize
			}
		};
		let selectedSizeValues = [];

		for (let item of measurementTable) {
			let sizeList = item.fitInfo['FIRST_FIT'].sizeValueList;
			for (let size of sizeList) {
				if (size.id === oldSizeInfo.id && size.value !== '') {
					selectedSizeValues.push({
						changeLogType: 'MEASUREMENT_VALUE_UPDATE',
						id: item.fitInfo['FIRST_FIT'].fitTypeId || null,
						dto: {
							pointOfMeasurementId: item.pomResponse.id,
							value: size.value || null,
							sizeId: selectedSizeToConvert.id
						}
					});
				}
			}
		}
		return [addChangeLogInfo, addBaseSize, convertedChangeLogInfo, ...selectedSizeValues];
	};

	const updateConvertedMeasurementTableData = (selectedSizeToConvert, oldSizeInfo, newSequenceNumber) => {
		const updatedMeasurementTable = measurementTable.map((item) => {
			const updatedItem = {
				...item,
				fitInfo: {
					...item.fitInfo,
					FIRST_FIT: {
						...item.fitInfo.FIRST_FIT,
						sizeValueList: item.fitInfo.FIRST_FIT.sizeValueList.map((size) => {
							if (size.id === oldSizeInfo.id) {
								return {
									...size,
									sizeCategoryId: selectedSizeToConvert.sizeCategoryId,
									id: selectedSizeToConvert.id,
									serial: selectedSizeToConvert.serial,
									label: selectedSizeToConvert.label,
									isBaseSize: oldSizeInfo.isBaseSize,
									isDisabled: selectedSizeToConvert.isDisabled,
									value: size.value,
									modified: size.modified,
									isNew: size.isNew,
									sizeCategorySerial: newSequenceNumber
								};
							}
							return size;
						})
					}
				}
			};

			return updatedItem;
		});

		return updatedMeasurementTable;
	};

	const dispatchConvertedGroupGrading = (sizeInfo) => {
		dispatch(addMeasurementGroupGrading({ groupGradingBaseSize: sizeInfo?.id }));
	};

	const dispatchBaseSizeUpdateOnSizeConvert = (fitId, sizeId) => {
		let changeLogInfo = {
			changeLogType: 'BASE_SIZE_UPDATE',
			id: fitId,
			dto: {
				sizeId: sizeId
			}
		};
		dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		dispatch(changeCostingPpSampleBaseSize({ baseSize: sizeId }));
	};

	const updateGroupGradingOnSizeConvert = (newConvertedSizeInfo, oldSizeInfo) => {
		let sortedSizes = fitWiseSizeList?.FIRST_FIT?.sizeList;
		let groupGradingBaseSize = sortedSizes?.find(
			(item) => item.id === fitWiseSizeList?.FIRST_FIT?.groupGradingBaseSize
		);
		if (groupGradingBaseSize) {
			if (oldSizeInfo.id === groupGradingBaseSize.id) {
				dispatchConvertedGroupGrading(newConvertedSizeInfo);
			} else {
				dispatchConvertedGroupGrading(groupGradingBaseSize);
			}
		}
	};

	const getSizeSequenceNumber = async (sizeCategoryId) => {
		try {
			dispatch(openLoader());
			const data = await increaseSizeCategorySequence(
				id,
				sizeCategoryId,
				selectedMeasurementType.constant
			);
			dispatch({
				type: Types.ADD_MEASUREMENT_SIZE_CATEGORY_SEQUENCE,
				payload: data
			});
			return data[sizeCategoryId];
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onSizeConvert = async (selectedSizeToConvert, item) => {
		let newSequenceNumber;
		const fitTypeId =
			measurementTable && measurementTable.length > 0
				? measurementTable[0]?.fitInfo['FIRST_FIT']?.fitTypeId
				: null;

		if (selectedSizeToConvert.sizeCategoryId in sizeCategorySequence) {
			newSequenceNumber = sizeCategorySequence[selectedSizeToConvert.sizeCategoryId];
		} else {
			newSequenceNumber = await getSizeSequenceNumber(selectedSizeToConvert.sizeCategoryId);
		}
		let updatedFitWiseSizeList = [...(fitWiseSizeList['FIRST_FIT']?.sizeList || [])];

		const findSelectedSizeIndex = updatedFitWiseSizeList.findIndex((size) => size.id === item.id);

		if (findSelectedSizeIndex !== -1) {
			updatedFitWiseSizeList[findSelectedSizeIndex] = {
				...selectedSizeToConvert,
				sizeCategorySerial: newSequenceNumber
			};
			const changeLogData = formatChangeLog(selectedSizeToConvert, fitTypeId, item);
			const convertedTableData = updateConvertedMeasurementTableData(
				selectedSizeToConvert,
				item,
				newSequenceNumber
			);
			dispatch(
				convertMeasurementSizeType({
					...fitWiseSizeList,
					FIRST_FIT: {
						sizeList: updatedFitWiseSizeList,
						baseSize: fitWiseSizeList['FIRST_FIT']?.baseSize
					}
				})
			);
			dispatch(updateSizeWiseValue(convertedTableData));
			dispatch(convertMeasurementSizeTypeChangeLog(changeLogData));
			updateGroupGradingOnSizeConvert(selectedSizeToConvert, item);
			if (fitWiseSizeList['FIRST_FIT']?.baseSize === item.id) {
				dispatchBaseSizeUpdateOnSizeConvert(fitTypeId, selectedSizeToConvert.id);
			}
		}
		setOpenSizeConvertDropdown(false);
	};
	const renderMeasurementTable = () => {
		let isEditableTable = isEditable();
		if (['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(selectedMeasurementType?.constant)) {
			return (
				<BuyerAndNitexMeasurementTable
					isShowSlideIcon={true}
					tableData={measurementTable}
					fitWiseSizeList={fitWiseSizeList}
					isMeasurementEditable={isEditableTable}
					hasShowActualAndDiffColumn={hasShowColumn}
					initialMeasurementTableData={initialMeasurementTableData}
					onDuplicateMeasurement={onDuplicateMeasurement}
					fitId={fitWiseSizeList['FIRST_FIT']?.fitTypeId}
					onReplacePOM={onReplacePOM}
					showReplaceDropdownId={showReplaceDropdownId}
					setShowReplaceDropdownId={setShowReplaceDropdownId}
				/>
			);
		} else if (['COSTING_GRADING', 'PP_SAMPLE'].includes(selectedMeasurementType?.constant)) {
			return (
				<CostingAndPPSampleTable
					isShowSlideIcon={true}
					tableData={measurementTable}
					fitWiseSizeList={fitWiseSizeList}
					isMeasurementEditable={isEditableTable}
					hasShowActualAndDiffColumn={hasShowColumn}
					sizeCategorySequence={sizeCategorySequence}
					selectedMeasurementType={selectedMeasurementType}
					onDuplicateMeasurement={onDuplicateMeasurement}
					unitType={unitType}
					onSizeConvert={onSizeConvert}
					isOpenSizeConvertDropdwon={isOpenSizeConvertDropdwon}
					setOpenSizeConvertDropdown={setOpenSizeConvertDropdown}
					onHideSizeColumn={onHideSizeColumn}
					hiddenColumnIds={hiddenSizeIds}
					onShowAllHiddenSizeColumn={onShowAllHiddenSizeColumn}
					parentComponent="MEASUREMENT"
					onReplacePOM={onReplacePOM}
					showReplaceDropdownId={showReplaceDropdownId}
					setShowReplaceDropdownId={setShowReplaceDropdownId}
				/>
			);
		} else {
			return <></>;
		}
	};

	const onClickLoadMeasurement = async (copyFromMeasurementType) => {
		try {
			dispatch(openLoader());
			const data = await copyMeasurementType(
				copyFromMeasurementType,
				selectedMeasurementType.constant,
				id
			);
			if (data.hasShowMSG) {
				toastInfo(data.msg);
			} else {
				await setSelectedMeasurementType(data.measurementType);
				await fetchMeasurementInfo();
				setIsMeasurementEditable(false);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderMeasurementLoadButton = () => {
		if (selectedMeasurementType?.constant === 'BUYER_FIT_DEVELOPMENT') {
			return (
				<div className="py-5 text-center">
					<button
						disabled={!isEditable()}
						className="button outline size36"
						onClick={() => onClickLoadMeasurement('NITEX_DEVELOPMENT')}
					>
						Load &quot;Nitex Development&quot; Chart
					</button>
				</div>
			);
		} else if (selectedMeasurementType?.constant === 'PP_SAMPLE') {
			return (
				<div className="py-5 text-center">
					<button
						disabled={!isEditable()}
						className="button outline size36"
						onClick={() => onClickLoadMeasurement('BUYER_FIT_DEVELOPMENT')}
					>
						Load &quot;Buyer Fit Development&quot; Chart
					</button>
				</div>
			);
		} else if (selectedMeasurementType?.constant === 'COSTING_GRADING') {
			return (
				<div className="py-5 text-center">
					<button
						className="button outline size36"
						disabled={!isEditable()}
						onClick={() => onClickLoadMeasurement('PP_SAMPLE')}
					>
						Load &quot;PP Sample&quot; Chart
					</button>
				</div>
			);
		} else if (selectedMeasurementType?.constant === 'NITEX_DEVELOPMENT') {
			return (
				<div className="py-5 text-center">
					<p className="regular-12 gray_dark_03 pb-3">No Size & Point Added </p>
					<button
						className="button outline size36"
						onClick={() => {
							setOpenPanel(true);
							if (isShowEditBtn()) {
								setIsMeasurementEditable(true);
							}
						}}
					>
						Add Size & Points
					</button>
				</div>
			);
		}
	};

	const onCloseConfirmation = () => {
		setConfirmationType('');
		setConfirmationTitle('');
		setIsShowConfirmation(false);
	};

	const getCurrentFitId = () => {
		if (measurementTable && measurementTable.length > 0) {
			let fitList = Object.keys(measurementTable[0]?.fitInfo);
			let lastFitTypeName = fitList[fitList.length - 1];
			return measurementTable[0].fitInfo[lastFitTypeName]?.fitTypeId;
		} else {
			return pomAndSizeList?.id;
		}
	};

	const onReviewMeasurement = async (status) => {
		try {
			dispatch(openLoader());
			await ApproveOrRejectMeasurementTable(getCurrentFitId(), status, id);
			await fetchMeasurementInfo();
			onCloseConfirmation();
			setIsMeasurementEditable(false);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const saveChangeLogs = async () => {
		try {
			let postData = {
				productId: id,
				unit: confirmationType === 'FORCE_UNIT_SAVE' ? tmpUnitType : pomAndSizeRef.current.unit,
				measurementType: pomAndSizeRef.current?.measurementType.constant,
				sizeCategoryId: pomAndSizeRef.current.sizeCategoryId,
				changeLogs: changeLogRef.current,
				id: getCurrentFitId() || null
			};
			const data = await saveMeasurementTable(postData);
			return data?.success;
		} catch (e) {
			printApiError(e);
		}
	};

	const handleConfirmationSubmit = async (actionType) => {
		if (actionType === 'YES') {
			if (confirmationType === 'CANCEL') {
				dispatch({
					type: Types.FETCH_PRODUCT_MEASUREMENT_TABLE_DATA,
					payload: initialMeasurementTableData
				});
				onCloseConfirmation();
				setIsMeasurementEditable(false);
				setShowReplaceDropdownId(null);
			} else if (
				confirmationType === 'SAVE' ||
				confirmationType === 'FORCE_SAVE' ||
				confirmationType === 'FORCE_UNIT_SAVE'
			) {
				try {
					dispatch(openLoader());
					const response = await saveChangeLogs();
					if (confirmationType === 'FORCE_SAVE' || confirmationType === 'FORCE_UNIT_SAVE') {
						setIsMeasurementEditable(false);
						if (confirmationType === 'FORCE_UNIT_SAVE') {
							await setUnitType(tmpUnitType);
						} else {
							await setSelectedMeasurementType(tmpSelectedMeasurementType);
						}
						await dispatch(clearMeasurementTable());
						await dispatch(fetchAllSizeCategories(`?productId=${id}`));
					} else {
						if (response) {
							await fetchMeasurementInfo();
						}
					}
					if (response) {
						await dispatch(clearMeasurementChangeLog());
						setIsMeasurementEditable(false);
					}
					onCloseConfirmation();
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			} else if (confirmationType === 'REVIEW') {
				await onReviewMeasurement('APPROVED');
			}
		} else {
			if (confirmationType === 'REVIEW') {
				await onReviewMeasurement('REJECTED');
			} else if (confirmationType === 'FORCE_SAVE') {
				if (selectedMeasurementType.constant !== tmpSelectedMeasurementType?.constant) {
					setIsMeasurementEditable(false);
					await setSelectedMeasurementType(tmpSelectedMeasurementType);
					await dispatch(clearMeasurementTable());
					await dispatch(fetchAllSizeCategories(`?productId=${id}`));
				}
				onCloseConfirmation();
			} else {
				onCloseConfirmation();
			}
		}
	};

	const handleOnFittingUpdate = async () => {
		try {
			dispatch(openLoader());
			setIsEditFittingInfo(false);
			await fetchFittingResponse();
			await fetchMeasurementInfo();
			dispatch(closeLoader());
			toastSuccess('Updated succesfully');
		} catch {
			dispatch(closeLoader());
		}
	};

	const isGarmentsAndQC = () => {
		return ['GARMENTS_TECH'].includes(getPrimaryUserType());
	};

	const isShowEditBtn = () => {
		if (['BUYER_FIT_DEVELOPMENT', 'PP_SAMPLE'].includes(selectedMeasurementType?.constant)) {
			return [
				'GARMENTS_TECH',
				'ADMIN',
				'THREE_D_DESIGNER',
				'SAMPLE_QC',
				'PATTERN_MASTER',
				'GRAPHICS_DESIGNER'
			].includes(getPrimaryUserType());
		} else if (['COSTING_GRADING'].includes(selectedMeasurementType?.constant)) {
			return [
				'GARMENTS_TECH',
				'ADMIN',
				'PATTERN_MASTER',
				'THREE_D_DESIGNER',
				'SAMPLE_QC',
				'GRAPHICS_DESIGNER'
			].includes(getPrimaryUserType());
		} else if (['NITEX_DEVELOPMENT'].includes(selectedMeasurementType?.constant)) {
			return [
				'GARMENTS_TECH',
				'ADMIN',
				'PATTERN_MASTER',
				'QA',
				'FASHION_DESIGNER',
				'THREE_D_DESIGNER',
				'SAMPLE_QC',
				'GRAPHICS_DESIGNER'
			].includes(getPrimaryUserType());
		}
	};

	const isShowReviewBtn = () => {
		let statusList = [];
		for (let item of initialMeasurementTableData) {
			statusList.push(item.status);
		}
		return (
			['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(selectedMeasurementType?.constant) &&
			statusList.includes('PENDING')
		);
	};

	const isFiveValueExist = () => {
		let items = [];
		if (measurementTable.length > 0) {
			let fitList = Object.keys(measurementTable[0].fitInfo);
			let currentFit = fitList[fitList.length - 1];
			for (let item of measurementTable) {
				if (items.length < 6) {
					let value = item.fitInfo[currentFit]?.sizeValueList[0]?.value;
					if (value) items.push(value);
				} else {
					break;
				}
			}
		}
		return items.length >= 5;
	};

	const handleDownloadInExcel = async () => {
		try {
			dispatch(openLoader());
			await downloadMeasurementInExcel(id, unitType, selectedMeasurementType?.constant);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const handleDownloadInPdf = async (e) => {
		e.stopPropagation();
		try {
			dispatch(openLoader());
			const data = await downloadMeasurementInPDF(id, unitType, selectedMeasurementType?.constant);
			window.open(data, '_parent');
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const isMeasurementUnitChanged = () => {
		return initialMeasurementUnit !== null && initialMeasurementUnit !== unitType;
	};

	const onUnitTypeChange = (value) => {
		setTmpUnitType(value);
		setConfirmationType('FORCE_UNIT_SAVE');
		setConfirmationTitle('Save the changes?');
		setIsShowConfirmation(true);
	};

	const onMeasurementTypeChange = () => {
		setConfirmationType('FORCE_SAVE');
		setConfirmationTitle('Save the changes?');
		setIsShowConfirmation(true);
	};

	const onClickSaveMeasurement = () => {
		setConfirmationType('SAVE');
		setConfirmationTitle('Save the changes?');
		setIsShowConfirmation(true);
	};

	const handleCurrentVersion = async () => {
		if (historyInfo?.id) {
			await fetchMeasurementInfo();
			await dispatch(setMeasurementHistoryInfo({}));
		}
		setOpenHistory(false);
	};

	useEffect(() => {
		if (designIdToView !== null) {
			openDesignViewModal();
		}
	}, [designIdToView]);

	const openDesignViewModal = () => {
		dispatch(
			setDesignViewModalInfo({
				id: designIdToView,
				onCloseViewModal: () => {
					setIsLoading(true);
					dispatch(fetchProductInfo(id)).finally(() => {
						setIsLoading(false);
					});
					setDesignIdToView(null);
					dispatch(clearDesignViewModalInfo({}));
				},
				source: 'PRODUCT_LIST'
			})
		);
	};

	const toggleMeasurementToggleColumn = async () => {
		const isShowDiffColumn = !hasShowColumn;

		const param = `?productId=${id}&showActual=${isShowDiffColumn}`;
		try {
			dispatch(openLoader());
			await setMeasurementShowColumn(param);
			await dispatch(toggleMeasurementActualAndDiffColumn(isShowDiffColumn));
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onToggleBlockUsedFromInfo = async () => {
		const isShowBlockInfo = !isShowBlockUsed;
		const param = `?productId=${id}&measurementType=${selectedMeasurementType.constant}&showBlockUsedFromInfo=${isShowBlockInfo}`;
		try {
			dispatch(openLoader());
			await setMeasurementShowColumn(param);
			dispatch(toggleBlockUsedFromInfo(isShowBlockInfo));
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onHideSizeColumn = async (selectedSizeId) => {
		const param = `?productId=${id}&measurementType=${selectedMeasurementType.constant}&hiddenSizeId=${selectedSizeId}`;
		try {
			dispatch(openLoader());
			await setMeasurementShowColumn(param);
			dispatch(hideMeasurementTableColumn([selectedSizeId]));
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onShowAllHiddenSizeColumn = async () => {
		try {
			dispatch(openLoader());
			const param = `?productId=${id}&measurementType=${selectedMeasurementType.constant}`;
			await showAllHiddenSizeColumn(param);
			dispatch(showAllMeasurementTableColumn());
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onEditMeasurement = async () => {
		if (initialMeasurementTableData?.[0]?.id) {
			setIsMeasurementEditable(true);
			dispatch(setInitMeasurementTableId(null));
		} else {
			try {
				dispatch(openLoader());
				const data = await initMeasurementTable(id, selectedMeasurementType?.constant, unitType);
				if (data) {
					dispatch(setInitMeasurementTableId(data));
					setIsMeasurementEditable(true);
				}
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const cloneFitInfoFromPrevRow = (pomResponse, brandDetailsResponseList) => {
		let isFitDevelopment = ['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(
			selectedMeasurementType?.constant
		);
		let fitInfo = {};

		const updateTolerance = (item) => {
			let value =
				brandDetailsResponseList?.find(
					(brand) =>
						brand?.brandResponse?.id === designToolStore?.brandId ||
						brand?.brandResponse?.id === ORGANIZTION_INFO?.id
				)?.tolerance || null;
			dispatch(
				saveProductMeasurementChangeLog(
					onAddChangeLog(item, 'TOLERANCE_UPDATE', 'value', value, item?.fitTypeId)
				)
			);
			return value;
		};

		if (Object.keys(fitWiseSizeList).length > 0) {
			for (let item in fitWiseSizeList) {
				let currentFit = fitWiseSizeList[item];
				fitInfo[item] = {
					pomResponseId: pomResponse.id,
					sizeValueList: currentFit.sizeList?.map((size) => ({ ...size, value: '' })),
					baseSize: currentFit.baseSize,
					fitTypeId: currentFit.fitTypeId
						? currentFit.fitTypeId
						: item === 'FIRST_FIT'
							? initialMeasurementTableData?.[0]?.id || initMeasurementId
							: null,
					actualValue: '',
					tolerance: updateTolerance({ currentFit, pointOfMeasurementId: pomResponse?.id }),
					gradingType:
						currentFit.groupGradingBaseSize || !isFitDevelopment
							? { name: 'Parallel & Proportional', constant: 'PARALLEL_PROPORTIONAL' }
							: undefined
				};
			}
		} else {
			fitInfo['FIRST_FIT'] = {
				pomResponseId: pomResponse.id,
				sizeValueList: [],
				baseSize: '',
				fitTypeId: initialMeasurementTableData?.[0]?.id || initMeasurementId,
				actualValue: '',
				tolerance: '',
				gradingType: !isFitDevelopment
					? { name: 'Parallel & Proportional', constant: 'PARALLEL_PROPORTIONAL' }
					: undefined
			};
		}
		return fitInfo;
	};

	const onPomClick = (e, selectedItem) => {
		let item = selectedItem?.pointOfMeasurement;
		let changeLogInfo = {
			changeLogType: e.target.checked ? 'POINT_ADD' : 'POINT_REMOVE',
			dto: {
				pointOfMeasurementId: item.id
			},
			id: initialMeasurementTableData?.[0]?.id || initMeasurementId
		};
		dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		if (!['NITEX_DEVELOPMENT', 'BUYER_FIT_DEVELOPMENT'].includes(selectedMeasurementType?.constant)) {
			let changeLogInfo = {
				changeLogType: 'GRADING_ACTION_CHANGE',
				id: initialMeasurementTableData?.[0]?.id || initMeasurementId,
				dto: {
					pointOfMeasurementId: item.id,
					gradingType: 'PARALLEL_PROPORTIONAL'
				}
			};
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}

		if (e.target.checked) {
			dispatch(
				saveNewPointInMeasurementTable({
					pomResponse: {
						id: item.id,
						name: item.name,
						categoryResponse: item.categoryResponse,
						pomCode: item?.pomCode || item.id,
						referenceId: item?.referenceId
					},
					fitInfo: cloneFitInfoFromPrevRow(item, selectedItem?.brandDetailsResponseList)
				})
			);
		} else {
			dispatch(removeLibraryPointInMeasurementTable(item.id));
		}
	};

	const onLoadPomTemplateClick = async () => {
		if (isEditable()) {
			try {
				dispatch(openLoader());
				let body = {
					id: getCurrentFitId() || initMeasurementId,
					productId: productId,
					sizeCategoryId: pomAndSizeList?.sizeCategoryId,
					measurementType: selectedMeasurementType?.constant,
					unit: unitType
				};
				await loadPomTemplate(body, designToolStore?.subCategoryResponse?.id);
				await fetchMeasurementInfo();
				setIsMeasurementEditable(false);
				await dispatch(clearMeasurementChangeLog());
				setOpenPanel(false);
				dispatch(closeLoader());
			} catch (error) {
				dispatch(closeLoader());
				toastError(error);
			}
		}
	};
	const isDisableSaveChange = () => {
		if (changeLog.length === 0) {
			return true;
		} else if (measurementTable.length === 0 && fitWiseSizeList['FIRST_FIT']?.sizeList.length === 0) {
			return true;
		} else return false;
	};

	const onReplacePOM = async (replacedPOMId, oldPOMId) => {
		const fitTypeId =
			measurementTable && measurementTable.length > 0
				? measurementTable[0]?.fitInfo['FIRST_FIT']?.fitTypeId
				: null;
		let changeLogInfo = {
			id: fitTypeId,
			changeLogType: 'POINT_REPLACE',
			dto: {
				pointOfMeasurementId: replacedPOMId,
				oldPointOfMeasurementId: oldPOMId
			}
		};
		await dispatch(replaceProductMeasurementChangeLog(changeLogInfo));
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="dashboard-container" ref={reference}>
				<div className="measurement-chart-section bg-gray">
					<div className="bg-gray">
						{/* Top Header Section */}
						<div className="measurement-filter d-flex justify-content-between align-items-center py-3">
							{/* Left header Version section */}
							<div className="left-half bg-white d-flex align-items-center">
								<p className="regular-14 gray_dark_01 ml-12">Version:</p>
								<div className="download-dropdown dropdown">
									<button
										className="button gray-outline size32"
										type="button"
										id="downloadType"
										data-toggle="dropdown"
										aria-expanded="false"
									>
										{selectedMeasurementType?.name}
										<img
											src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
											alt="download"
											className="ml-2"
										/>
									</button>
									<ul
										className="dropdown-menu dropdown-menu-right shadow-lg"
										role="menu"
										aria-labelledby="downloadType"
									>
										{measurementTypes?.map((item) => {
											return (
												<li
													key={`measurement_${item.constant}`}
													onClick={async () => {
														setCurrentMeasurementType(id, item.constant);
														if (changeLog.length > 0) {
															onMeasurementTypeChange();
															setTmpSelectedMeasurementType(item);
														} else {
															if (
																selectedMeasurementType?.constant !==
																item?.constant
															) {
																await dispatch(clearMeasurementTable());
															}
															setIsMeasurementEditable(false);
															setSelectedMeasurementType(item);
															await dispatch(
																fetchAllSizeCategories(`?productId=${id}`)
															);
														}
													}}
												>
													<span className="text-nowrap">{item.name}</span>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
							{/* Right header section */}
							<div className="right-half d-flex align-items-center templates-half">
								<div className="size-switch-buttons">
									<button
										disabled={!isMeasurementEditable}
										className={unitType === 'CM' ? 'selected' : ''}
										onClick={() => {
											onUnitTypeChange('CM');
										}}
									>
										CM
									</button>
									<button
										disabled={!isMeasurementEditable}
										className={unitType === 'INCH' ? 'selected' : ''}
										onClick={() => {
											onUnitTypeChange('INCH');
										}}
									>
										IN
									</button>
								</div>
								<div className="save-template-btn text-right ml-2">
									{/* {!selectedDesignData?.blockDetailsResponse?.isBlock ? (
										<button
											className="button outline size36"
											onClick={() => dispatch(setCurrentBlockAction('SAVE'))}
										>
											Save as Block
										</button>
									) : (
										<button
											className="button outline size36"
											onClick={() => dispatch(setCurrentBlockAction('EDIT'))}
										>
											Edit Block
										</button>
									)} */}
									<div className="download-dropdown dropdown ml-2">
										<button
											className="button outline size36"
											type="button"
											id="downloadType"
											data-toggle="dropdown"
											aria-expanded="false"
										>
											<img
												src={`${process.env.PUBLIC_URL}/icons/download-dark-20.svg`}
												alt="download"
												className="mr-1"
											/>
											Download
										</button>
										<ul
											className="dropdown-menu dropdown-menu-right shadow-lg"
											role="menu"
											aria-labelledby="downloadType"
										>
											<li onClick={handleDownloadInPdf}>
												<span>PDF</span>
											</li>
											<li onClick={handleDownloadInExcel}>
												<span>Excel </span>
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
						{/* *********************** */}

						{/* Header Lower Section */}
						<div className="measurement-chart-actions d-flex flex-wrap justify-content-between px-3 py-2 bg-white mb-3">
							<div className="actions">
								<div className="d-flex flex-wrap align-items-center">
									<button
										className="button gray-outline size32 mr-12"
										onClick={() => {
											setOpenPanel(true);
											if (!isMeasurementEditable && isShowEditBtn()) {
												toastWarning('Click edit button to make changes');
											}
										}}
									>
										<img
											src={`${
												!openPanel
													? process.env.PUBLIC_URL + '/icons/Left-arrow.svg'
													: process.env.PUBLIC_URL + '/icons/right-arrow.svg'
											}`}
											alt="arrow"
										/>
										Add size & points
									</button>

									<button
										className="button update-fitting-button size32 mr-12"
										onClick={() => {
											if (changeLog.length > 0) {
												onClickSaveMeasurement();
											} else {
												setIsEditFittingInfo(true);
											}
										}}
									>
										{pomAndSizeList?.fittingName || 'Update block info'}
									</button>
									{changeLog.length === 0 &&
										isShowEditBtn() &&
										!isMeasurementUnitChanged() &&
										!isMeasurementEditable && (
											<button
												className="button size32 mr-12"
												onClick={onEditMeasurement}
											>
												Edit Measurement
											</button>
										)}
									{(changeLog.length > 0 ||
										isMeasurementUnitChanged() ||
										isMeasurementEditable) && (
										<button
											className="button size32 mr-12 btn-success"
											onClick={onClickSaveMeasurement}
											disabled={isDisableSaveChange()}
										>
											Save Measurement
										</button>
									)}
									{(changeLog.length > 0 || isMeasurementEditable) && (
										<button
											className="button gray-outline size32 mr-12"
											onClick={() => {
												if (changeLog.length > 0) {
													setConfirmationType('CANCEL');
													setConfirmationTitle('Cancel changes?');
													setIsShowConfirmation(true);
												} else {
													dispatch({
														type: Types.FETCH_PRODUCT_MEASUREMENT_TABLE_DATA,
														payload: initialMeasurementTableData
													});
													setIsMeasurementEditable(false);
													setShowReplaceDropdownId(null);
												}
											}}
										>
											Cancel
										</button>
									)}
									{isGarmentsAndQC() && isShowReviewBtn() && isFiveValueExist() && (
										<button
											className="button gray-outline size32 mr-12"
											disabled={changeLog.length > 0}
											onClick={() => {
												setConfirmationType('REVIEW');
												setConfirmationTitle('What is your review result?');
												setIsShowConfirmation(true);
											}}
										>
											Review
										</button>
									)}
								</div>
							</div>
							<div className="history d-flex align-items-center">
								{historyInfo?.id && (
									<p className="regular-12 gray_dark_03 mr-3 mt-1">
										{onGetLocalFromUtc(historyInfo?.createdAt, 'MMM DD, YYYY')} -{' '}
										{onGetLocalFromUtc(historyInfo?.createdAt, 'h:mm a')}
									</p>
								)}
								<div className="load-measurement dropdown">
									<button
										className="button text-only size32"
										type="button"
										id="downloadType"
										data-toggle="dropdown"
										aria-expanded="false"
										disabled={!isMeasurementEditable}
									>
										<span className="gray_dark_02">Load measurement</span>
										<img
											src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
											alt="download"
											className="ml-2"
										/>
									</button>
									<ul
										className="dropdown-menu dropdown-menu-right shadow-lg"
										role="menu"
										aria-labelledby="downloadType"
									>
										{measurementTypes?.map((item) => {
											return (
												<li
													key={`measurement_${item.constant}`}
													onClick={async () => {
														await onClickLoadMeasurement(item.constant);
													}}
												>
													<span className="text-nowrap">{item.name}</span>
												</li>
											);
										})}
									</ul>
								</div>
								<div className="load-measurement dropdown">
									<button
										className="button text-only size32"
										type="button"
										id="downloadType"
										data-toggle="dropdown"
										aria-expanded="false"
									>
										<img
											src={`${process.env.PUBLIC_URL}/icons/more_vertical.svg`}
											alt="download"
											className="ml-3"
										/>
									</button>
									<ul
										className="dropdown-menu dropdown-menu-right shadow-lg"
										role="menu"
										aria-labelledby="downloadType"
									>
										<li>
											<span
												className="text-nowrap"
												onClick={() => setOpenHistory(true)}
											>
												View History
											</span>
										</li>
										<li onClick={toggleMeasurementToggleColumn}>
											<span className="text-nowrap">
												{hasShowColumn ? 'Hide' : 'Show'} Actual & deference column
											</span>
										</li>
										<li onClick={onToggleBlockUsedFromInfo}>
											<span className="text-nowrap">
												{isShowBlockUsed ? 'Hide' : 'Show'} Block used from info
											</span>
										</li>
									</ul>
								</div>
							</div>
						</div>
						{/* *********************** */}
					</div>
					{pomAndSizeList?.measurementCopyFromResponse && isShowBlockUsed && (
						<div className="d-flex justify-content-end mb-2 mr-1">
							<span className="regular-14">
								Block used from:{' '}
								<span
									className="text-underline cursor-pointer"
									onClick={() =>
										setDesignIdToView(pomAndSizeList?.measurementCopyFromResponse?.id)
									}
								>
									{pomAndSizeList?.measurementCopyFromResponse?.blockDetailsResponse
										?.blockTitle ||
										pomAndSizeList?.measurementCopyFromResponse?.referenceNumber}
								</span>
							</span>
						</div>
					)}
					<section className="filter-data-container design-edit-filter-container px-0 pt-0">
						<div
							id="measurement-sidebar"
							className={openPanel ? 'scroll-y-label show-sidebar' : 'scroll-y-label'}
						>
							{openPanel && (
								<AddPomFromRightPnael
									selectedPomIds={selectedPomResponseIds}
									onCheckBoxClick={(e, selectedItem) => {
										if (isEditable()) {
											onPomClick(e, selectedItem);
										}
									}}
									pomTemplateDetails={null}
									onClose={() => setOpenPanel(false)}
									fromPage="MEASUREMENT"
									onLoadPomTemplateClick={onLoadPomTemplateClick}
									isEditable={isEditable}
								/>
							)}
						</div>

						<div className="data-table measurement-data-table">
							{openConfirmationModal && (
								<DeleteModal
									onRemove={() => onRemovePom(productId, pomId)}
									onClose={() => setConfirmationModal(false)}
								/>
							)}
							{(measurementTable.length > 0 ||
								fitWiseSizeList['FIRST_FIT']?.sizeList?.length > 0) &&
								renderMeasurementTable()}
							{!measurementTable.length > 0 &&
								!fitWiseSizeList['FIRST_FIT']?.sizeList?.length > 0 &&
								!initialMeasurementTableData.length > 0 &&
								renderMeasurementLoadButton()}
							{isEdit && (
								<NitexImageCropper
									isEdit={isEdit}
									onClose={() => setIsEdit(false)}
									imageSrc={
										measurementImage?.base64Str
											? measurementImage.base64Str
											: measurementImage?.docUrl
									}
									onSave={onCroppedDataSave}
								/>
							)}
							{isEditFittingInfo && (
								<UpdateFittingModal
									productId={id}
									isShowClose={true}
									blockResponse={fittingResponse}
									onAddSuccess={handleOnFittingUpdate}
									onCloseModal={() => setIsEditFittingInfo(false)}
									openedFrom={'measurement'}
									isBottomCategory={isBottomCategory}
								/>
							)}
							{isDelete && (
								<DeleteModal
									onRemove={() => handleDelete()}
									onClose={() => setIsDelete(false)}
								/>
							)}
						</div>
						{openHistory && (
							<ViewHistory
								handleCurrentVersion={handleCurrentVersion}
								onRestoreHistory={onRestoreHistory}
								historyList={historyList}
							/>
						)}
					</section>
					<BlockSection
						isEditable={isEditable()}
						fittingResponse={fittingResponse}
						selectedMeasurementType={selectedMeasurementType}
					/>
					{isShowConfirmation && (
						<ConfirmationModal
							type={confirmationType}
							title={confirmationTitle}
							onClose={onCloseConfirmation}
							onSubmit={handleConfirmationSubmit}
						/>
					)}
				</div>
			</div>
		</LoaderComponent>
	);
};

export default Measurement;
